import React, { useEffect, useState, useRef } from 'react';
import { Mail, Phone, Clock, ArrowRight, Send, MapPin } from 'lucide-react';
import BookingModule from '@/components/sections/BookingModule';
import EstimationForm from '@/components/sections/EstimationForm';
import { COMPANY_INFO } from '@/lib/constants';
import { motion, useScroll, useTransform } from 'framer-motion';
import Button from '@/components/ui/Button';
import SEO from '@/components/SEO';

const Contact = () => {
    const [isVisible, setIsVisible] = useState(false);
    const heroRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setIsVisible(true);
        
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );

        if (heroRef.current) {
            observer.observe(heroRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                when: "beforeChildren",
                staggerChildren: 0.2,
                duration: 0.6
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.5 }
        }
    };

    // Fixed the pulseVariants type by explicitly using "loop" as a literal type
    const pulseVariants = {
        pulse: {
            scale: [1, 1.05, 1],
            opacity: [0.7, 1, 0.7],
            transition: {
                duration: 2,
                repeat: Infinity,
                repeatType: "loop" as const // Using "loop" as a const assertion to fix the type
            }
        }
    };

    return (
        <>
            <SEO 
                title="Contact"
                description="Contactez Orcsys pour discuter de votre projet digital. Notre équipe est à votre écoute pour vous accompagner dans la réalisation de vos objectifs."
                keywords="contact, devis, consultation, projet digital, développement web, développement mobile, estimation projet, rendez-vous, accompagnement"
                type="contact"
                url="https://innov.orcsys.fr/contact"
                image="https://innov.orcsys.fr/images/contact.jpg"
                section="Contact"
            />
            <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
                {/* Hero Section */}
                <section ref={heroRef} className="relative pt-32 pb-24 px-4 overflow-hidden">
                    {/* Background elements */}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(142,227,205,0.3)_0%,rgba(255,255,255,0)_60%)]" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(155,135,245,0.2)_0%,rgba(255,255,255,0)_60%)]" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-[url('https://images.unsplash.com/photo-1557683316-973673baf926?q=80&w=2029&auto=format&fit=crop')] opacity-[0.03] bg-no-repeat bg-center bg-cover" />

                    <motion.div 
                        className="container mx-auto"
                        initial="hidden"
                        animate={isVisible ? "visible" : "hidden"}
                        variants={containerVariants}
                    >
                        <motion.h1 
                            className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 text-center"
                            variants={itemVariants}
                        >
                            <span className="relative inline-block">
                                Contactez
                                <motion.span 
                                    className="absolute -bottom-1 left-0 w-full h-2 bg-orcsys-400/30 rounded-full"
                                    animate={{ 
                                        width: ["0%", "100%"],
                                        opacity: [0, 1] 
                                    }}
                                    transition={{ 
                                        duration: 1,
                                        delay: 0.8
                                    }}
                                />
                            </span>{" "}
                            <span className="text-orcsys-500">nous</span>
                        </motion.h1>
                        
                        <motion.p 
                            className="text-lg text-slate-600 max-w-2xl mx-auto text-center mb-10"
                            variants={itemVariants}
                        >
                            Nous sommes là pour répondre à toutes vos questions et vous accompagner dans vos projets.
                            Transformez vos idées en solutions concrètes dès aujourd'hui.
                        </motion.p>

                        <div 
                            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto"
                        
                        >
                            {/* Email */}
                            <motion.div
                                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group border border-slate-100 relative overflow-hidden"
                                variants={itemVariants}
                                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                            >
                                <motion.div 
                                    className="absolute inset-0 bg-gradient-to-br from-orcsys-50/50 to-transparent opacity-0 
                                    group-hover:opacity-100 transition-opacity duration-300" 
                                />

                                <motion.div
                                    className="w-14 h-14 rounded-lg bg-orcsys-50 flex items-center justify-center mb-5 
                                    group-hover:bg-orcsys-100 transition-colors relative z-10"
                                    variants={pulseVariants}
                                    animate="pulse"
                                >
                                    <Mail className="w-6 h-6 text-orcsys-500" />
                                </motion.div>
                                
                                <h3 className="text-xl font-semibold text-slate-900 mb-3 relative z-10">Email</h3>
                                <p className="text-slate-500 mb-4 relative z-10">Contactez-nous par email pour toute demande</p>
                                <a 
                                    href={`mailto:${COMPANY_INFO.email}`} 
                                    className="text-orcsys-500 hover:text-orcsys-600 font-medium flex items-center group/link relative z-10"
                                >
                                    {COMPANY_INFO.email}
                                    <ArrowRight className="ml-2 w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                                </a>
                            </motion.div>

                            {/* Phone */}
                            <motion.div
                                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group border border-slate-100 relative overflow-hidden"
                                variants={itemVariants}
                                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                            >
                                <motion.div 
                                    className="absolute inset-0 bg-gradient-to-br from-orcsys-50/50 to-transparent opacity-0 
                                    group-hover:opacity-100 transition-opacity duration-300" 
                                />

                                <motion.div
                                    className="w-14 h-14 rounded-lg bg-orcsys-50 flex items-center justify-center mb-5 
                                    group-hover:bg-orcsys-100 transition-colors relative z-10"
                                    variants={pulseVariants}
                                    animate="pulse"
                                >
                                    <Phone className="w-6 h-6 text-orcsys-500" />
                                </motion.div>
                                
                                <h3 className="text-xl font-semibold text-slate-900 mb-3 relative z-10">Téléphone</h3>
                                <p className="text-slate-500 mb-4 relative z-10">Nous sommes à votre écoute</p>
                                <a 
                                    href={`tel:${COMPANY_INFO.phone}`} 
                                    className="text-orcsys-500 hover:text-orcsys-600 font-medium flex items-center group/link relative z-10"
                                >
                                    {COMPANY_INFO.phone}
                                    <ArrowRight className="ml-2 w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                                </a>
                            </motion.div>

                            {/* Address */}
                            <motion.div
                                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group border border-slate-100 relative overflow-hidden"
                                variants={itemVariants}
                                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                            >
                                <motion.div 
                                    className="absolute inset-0 bg-gradient-to-br from-orcsys-50/50 to-transparent opacity-0 
                                    group-hover:opacity-100 transition-opacity duration-300" 
                                />

                                <motion.div
                                    className="w-14 h-14 rounded-lg bg-orcsys-50 flex items-center justify-center mb-5 
                                    group-hover:bg-orcsys-100 transition-colors relative z-10"
                                    variants={pulseVariants}
                                    animate="pulse"
                                >
                                    <Clock className="w-6 h-6 text-orcsys-500" />
                                </motion.div>
                                
                                <h3 className="text-xl font-semibold text-slate-900 mb-3 relative z-10">Horaires</h3>
                                <p className="text-slate-500 mb-4 relative z-10">Notre équipe est disponible</p>
                                <div className="text-orcsys-500 font-medium relative z-10">
                                    {COMPANY_INFO.hours.days}<br />
                                    {COMPANY_INFO.hours.time}
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </section>

                {/* Floating elements */}
                <div className="absolute top-1/4 left-[10%] w-16 h-16 bg-orcsys-50/30 rounded-full blur-2xl" />
                <div className="absolute bottom-1/4 right-[10%] w-20 h-20 bg-orcsys-100/20 rounded-full blur-2xl" />
                <div className="absolute top-1/2 right-[15%] w-8 h-8 bg-orcsys-500/30 rounded-full blur-xl" />
                <div className="absolute bottom-1/3 left-[20%] w-12 h-12 bg-orcsys-500/20 rounded-full blur-xl" />
            </div>

            {/* Booking Section */}
            <div id="booking-section">
                <BookingModule />
            </div>

            {/* Estimation Section */}
            <EstimationForm />
        </>
    );
};

export default Contact;
